import { OutlineButton, PrimaryButton } from "@components/Buttons";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import queryKeys from "../../configs/react-query/query-key";
import { PermissionContext } from "../../contexts/PermissionContext";
import { useMutationData } from "../../hooks/useMutationData";
import { useSbomVerificationRequest } from "../../hooks/useSbomVerificationRequest";
import { useTheme } from "@hooks/useTheme";
import CalendarIcon from "../Icons/CalendarIcon";
import CheckedIcon from "../Icons/CheckedIcon";
import SbomVerificationRequest from "../Project/SbomVerificationRequest";
import { ConfirmActionModal } from "./index";
import SbomClarityCompareModal from "./SbomClarityCompareModal";
import FullScreenDotLoader from "./FullScreenDotLoader";
import Spin from "@components/UI/Spin";
import { useSearchInfo } from "@hooks/useSearchInfo";
import DotLoader from "react-spinners/DotLoader";
import { createWorkflowTestId, workflowStepSlug } from "@utils/workflowTestId";
import { CustomDatePicker } from "@components/Forms";

const WorkflowProgressSbom = ({
  steps = [],
  currentStep = 0,
  className = "",
  projectManagerId,
  onDecompleteFromDone,
  onRequestReVerification,
  onMoveToDone,
  showCompareModal = false,
  onSelectClarityVerification,
  sbomVerificationData = null,
  projectData = null,
  isLoading = false,
  isFetching = false,
  sbomVerificationSeq = null,
  dataTestId = "workflow-progress-sbom",
}) => {
  const { colors } = useTheme();
  const tid = createWorkflowTestId(dataTestId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectSeq } = useParams();
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const canManageWorkflow = canManageProjectWorkflow(projectManagerId ?? projectData?.projectManagerId);
  const { handleReUploadVerification, isDuplicateCheckPending, isReUploadPending } = useSbomVerificationRequest();
  const { scanToolList, fetchSearchInfo } = useSearchInfo();

  // Add state for processing/loading
  const [processingRowId, setProcessingRowId] = useState(null);

  /* Loading state for workflow buttons */
  // Review step loading states
  const [isRequestReVerificationLoading, setIsRequestReVerificationLoading] = useState(false);
  const [isMoveToDoneLoading, setIsMoveToDoneLoading] = useState(false);
  // Ready step loading states
  const [isDeCompleteVerificationLoading, setIsDeCompleteVerificationLoading] = useState(false);


  // Check if clarity scan tool is enabled
  const isClarityEnabled = () => {
    if (!scanToolList || !Array.isArray(scanToolList) || scanToolList.length === 0) return false;
    return scanToolList.some(tool => tool.value === "clarity");
  };

  // Fetch search info when component mounts
  useEffect(() => {
    fetchSearchInfo();
  }, [fetchSearchInfo]);

  // Mutation for updating SBOM verification (PUT request)
  const updateSbomVerification = useMutationData(
    queryKeys.createSbomVerification,
    {
      method: "PUT",
    },
    {
      onError: (error) => {
        console.error("Error during update verification:", error);
      },
    },
  );

  // Mutation for downloading SBOM verification result
  const downloadSbomVerificationResultMutation = useMutationData(
    "/sbomVerificationDownloadResult",
    {
      method: "GET",
      responseType: "blob",
      params: {
        sbomVerificationSeq,
      },
    },
    {
      onSuccess: (response) => {
        if (response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sbom_verification_result_${sbomVerificationSeq}.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      },
      onError: (error) => {
        console.error("Error downloading SBOM verification result:", error);
      },
    },
  );

  // Handler for downloading SBOM verification result
  const handleDownloadSbomVerificationResult = () => {
    if (sbomVerificationSeq) {
      downloadSbomVerificationResultMutation.mutate();
    }
  };

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });

  // State for re-verification modal with datetime picker
  const [reVerificationModal, setReVerificationModal] = useState({
    isOpen: false,
    selectedDateTime: new Date(),
  });

  // State for SBOM verification request modal
  const [sbomModal, setSbomModal] = useState({
    isOpen: false,
  });

  // Duplicate checksum confirmation modal
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    pendingUpload: null,
  });

  // State for clarity comparison modal - sync with prop from parent
  const [showClarityCompareModal, setShowClarityCompareModal] = useState(showCompareModal);

  // Sync state with prop changes
  useEffect(() => {
    setShowClarityCompareModal(showCompareModal);
  }, [showCompareModal]);

  // Handle SBOM re-upload - call all 3 APIs
  const handleSbomReUpload = async (data) => {
    const { message, verifFile, scanTypeCd, sbomSeq, sbomType } = data;

    // Handle duplicate check and processing
    const onDuplicateFound = (proceedCallback) => {
      // Store the upload function to call later if user confirms
      setDuplicateModal({
        isOpen: true,
        pendingUpload: proceedCallback,
      });
    };

    try {
      // Use the new re-upload functionality that creates new verification records
      await handleReUploadVerification(
        {
          projectSeq,
          projectId: projectData?.projectId || "unknown",
          requestComment: message || "",
          verifFile: data.verifFile,
          scanTypeCd: scanTypeCd || "SBOM",
          sbomSeq: sbomSeq,
          sbomType: sbomType,
          sbomDetect: data.sbomDetect,
        },
        onDuplicateFound,
        () => {
          // Navigate back to project detail after successful re-upload
          navigate(`/projects/${projectSeq}`, { state: { activeTab: 'sbom' } });
        },
        setProcessingRowId
      );
    } catch (error) {
      console.error("Error during SBOM re-upload:", error);
    }
  };

  const formatModalTitle = (title) => (typeof title === "string" ? title.toUpperCase() : title);

  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: formatModalTitle(title),
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "current";
    return "pending";
  };

  const getConnectorClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "h-1 flex-1 transition-all duration-200";
    if (status === "completed") {
      return `${baseClasses} ${colors.bgStatusSuccess}`;
    }
    return `${baseClasses} ${colors.bgStatusMuted}`;
  };

  // Function to check if step should display CheckedIcon
  const shouldShowCheckedIcon = (stepIndex, step) => {
    // Don't show any icon for step 0 (Request ReVerification)
    if (stepIndex === 0) {
      return false;
    }

    const status = getStepStatus(stepIndex);
    // Display CheckedIcon for completed steps OR Ready step when it's the current step
    return status === "completed" || (status === "current" && step.label === "Ready");
  };

  // Re-verification handlers
  const handleRequestReVerificationClick = () => {
    // Check if we're in re-verification stage (step 0)
    if (currentStep === 0) {
      // For re-verification stage, show a simple confirmation without datetime picker
      showConfirmation(
        t("common.re-verification-title"),
        t("common.re-verification-confirm"),
        t("common.re-verification-title"),
        "warning",
        async () => {
          setIsRequestReVerificationLoading(true);
          try {
            if (onRequestReVerification) {
              await onRequestReVerification(null);
            }
          } catch (error) {
            console.error("Error in onRequestReVerification:", error);
          } finally {
            setIsRequestReVerificationLoading(false);
          }
        },
      );
    } else {
      // For other stages, show modal with datetime picker
      setReVerificationModal({
        isOpen: true,
        selectedDateTime: new Date(),
      });
    }
  };

  // Handle confirmation from re-verification modal (similar to ProgressBarSbom)
  const handleConfirmRequestReVerification = async (selectedDate) => {
    if (isRequestReVerificationLoading) return;
    
    setIsRequestReVerificationLoading(true);
    try {
      if (onRequestReVerification) {
        await onRequestReVerification(selectedDate);
      }
    } catch (error) {
      console.error("Error in onRequestReVerification:", error);
    } finally {
      setIsRequestReVerificationLoading(false);
    }
    closeReVerificationModal();
  };

  const closeReVerificationModal = () => {
    setReVerificationModal({
      isOpen: false,
      selectedDateTime: new Date(),
    });
  };

  const handleSbomModalOpen = () => {
    if (processingRowId !== null) return; // Prevent opening modal while processing
    setSbomModal({ isOpen: true });
  };
  const handleSbomModalClose = () => {
    if (processingRowId !== null) return; // Prevent closing modal while processing
    setSbomModal({ isOpen: false });
  };

  // Duplicate checksum modal handlers
  const handleDuplicateModalClose = () => setDuplicateModal({ isOpen: false, pendingUpload: null });
  
  const handleDuplicateConfirm = () => {
    if (duplicateModal.pendingUpload) duplicateModal.pendingUpload();
    handleDuplicateModalClose();
  };
  // Handle datetime change in re-verification modal
  const handleDateTimeChange = (newDateTime) => {
    setReVerificationModal((prev) => ({
      ...prev,
      selectedDateTime: newDateTime,
    }));
  };

  // Handle compare with clarity button click
  const handleCompareWithClarityClick = () => {
    setShowClarityCompareModal(true);
  };

  // Handle clarity verification selection
  const handleSelectClarityVerification = (verification) => {
    setShowClarityCompareModal(false);
    if (onSelectClarityVerification) {
      onSelectClarityVerification(verification);
    }
  };

  // Handle close clarity modal
  const handleCloseClarityModal = () => {
    setShowClarityCompareModal(false);
  };

  // Handle ESC key for re-verification modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && reVerificationModal.isOpen) {
        closeReVerificationModal();
      }
    };

    if (reVerificationModal.isOpen) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [reVerificationModal.isOpen]);

  // Function to render buttons based on current step
  const renderStepButtons = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return null;

    const stepLabel = currentStepData.label;
    const stepSlug = workflowStepSlug(stepLabel);
    const stid = (name) => tid(`step-${stepSlug}-${name}`);

    switch (stepLabel) {
      case "Request ReVerification":
        return (
          <div className="flex justify-end items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {/* Download button */}
              {sbomVerificationSeq && (
                <OutlineButton
                  data-testid={stid("download-result-button")}
                  size="lg"
                  onClick={handleDownloadSbomVerificationResult}
                  disabled={downloadSbomVerificationResultMutation.isPending}
                >
                  {downloadSbomVerificationResultMutation.isPending && <Spin className="mr-2" />}
                  {t("common.download-sbom-verification-result")}
                </OutlineButton>
              )}
              <PrimaryButton
                data-testid={stid("re-upload-button")}
                size="lg"
                onClick={handleSbomModalOpen}
                disabled={processingRowId !== null}
              >
                {t("common.re-upload-button")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Review":
        const hasMissingData =
          sbomVerificationData?.missings &&
          (Array.isArray(sbomVerificationData.missings)
            ? sbomVerificationData.missings.length > 0
            : sbomVerificationData.missings !== "" && sbomVerificationData.missings !== null);

        const isReviewActionLoading = isRequestReVerificationLoading || isMoveToDoneLoading;

        return (
          <div className="flex justify-end items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {/* Request Re-Verif / Complete / De-complete — SUPERUSER, MANAGER, PROJECT_MANAGER only */}
              {canManageWorkflow && (
                <OutlineButton
                  data-testid={stid("request-re-verification-button")}
                  size="lg"
                  onClick={handleRequestReVerificationClick}
                  disabled={isReviewActionLoading}
                  className={isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {t("common.Request-Re-Verif")}
                </OutlineButton>
              )}

              {/* Download button */}
              {sbomVerificationSeq && (
                <OutlineButton
                  data-testid={stid("download-result-button")}
                  size="lg"
                  onClick={handleDownloadSbomVerificationResult}
                  disabled={isReviewActionLoading || downloadSbomVerificationResultMutation.isPending}
                >
                  {downloadSbomVerificationResultMutation.isPending && <Spin className="mr-2" />}
                  {t("common.download-sbom-verification-result")}
                </OutlineButton>
              )}

              {/* Show Complete Verification button if file format is valid */}
              {canManageWorkflow && hasMissingData && (
                <PrimaryButton
                  data-testid={stid("complete-verification-button")}
                  size="lg"
                  disabled={isReviewActionLoading}
                  onClick={() =>
                    showConfirmation(
                      t("common.complete-verification-title"),
                      t("common.complete-verification-confirm"),
                      t("common.complete-verification"),
                      "primary",
                      async () => {
                        // Set loading state immediately when user confirms
                        setIsMoveToDoneLoading(true);
                        try {
                          if (onMoveToDone) {
                            await onMoveToDone();
                          }
                        } catch (error) {
                          console.error("Error in onMoveToDone:", error);
                        } finally {
                          setIsMoveToDoneLoading(false);
                        }
                      },
                    )
                  }
                >
                  {isMoveToDoneLoading && <Spin className="mr-2" />}
                  {t("common.complete-verification")}
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      case "Ready":
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("decomplete-verification-button")}
                  disabled={isDeCompleteVerificationLoading}
                  onClick={() => {
                    showConfirmation(
                      t("sbom-status-options.decomplete-verification"),
                      t("sbom-status-options.are-you-sure-decomplete"),
                      t("sbom-status-options.decomplete-verification"),
                      "danger",
                      async () => {
                        // Set loading state immediately when user confirms
                        setIsDeCompleteVerificationLoading(true);
                        try {
                          if (onDecompleteFromDone) {
                            await onDecompleteFromDone();
                          }
                        } catch (error) {
                          console.error("Error in onDecompleteFromDone:", error);
                        } finally {
                          setIsDeCompleteVerificationLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isDeCompleteVerificationLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isDeCompleteVerificationLoading && <Spin className="mr-2" />}
                  {t("common.workflow.decomplete-verif")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {/* Download button */}
              {sbomVerificationSeq && (
                <OutlineButton
                  data-testid={stid("download-result-button")}
                  size="lg"
                  onClick={handleDownloadSbomVerificationResult}
                  disabled={isDeCompleteVerificationLoading || downloadSbomVerificationResultMutation.isPending}
                >
                  {downloadSbomVerificationResultMutation.isPending && <Spin className="mr-2" />}
                  {t("common.download-sbom-verification-result")}
                </OutlineButton>
              )}

              {isClarityEnabled() && (
                <PrimaryButton
                  data-testid={stid("compare-with-clarity-button")}
                  size="lg"
                  onClick={handleCompareWithClarityClick}
                  disabled={isDeCompleteVerificationLoading}
                >
                  {t("common.compare-with-clarity")}
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex justify-between items-center mt-4">
            <div className="flex gap-2"></div>
          </div>
        );
    }
  };

  const isAnyLoading = isLoading || isFetching;

  return (
    <div className={`w-full h-40 relative ${className}`} data-testid={tid("root")}>
      {isAnyLoading && (
        <div
          data-testid={tid("loading-overlay")}
          className={`absolute inset-0 z-50 flex flex-col items-center justify-center ${colors.bgPrimary} rounded-lg`}
        >
          <DotLoader
            color={colors.textActiveColor}
            loading={isAnyLoading}
            size={60}
            speedMultiplier={1.2}
            aria-label="Loading"
          />
        </div>
      )}
      {currentStep === 0 ? (
        // Show special interface for step 0 (Request ReVerification)
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-12" data-testid={tid("re-verification-step")}>
            <div className="relative flex flex-col items-center">
              <div>
                <div className={`w-14 h-14 ${colors.textSecondary} flex items-center justify-center opacity-60`}>
                  <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Git-like branching icon with three circles connected by lines */}
                    <circle cx="16" cy="16" r="5" fill="currentColor" />
                    <circle cx="16" cy="48" r="5" fill="currentColor" />
                    <circle cx="48" cy="48" r="5" fill="currentColor" />
                    <path
                      d="M16 21 L16 43 M16 43 L48 43"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 text-center text-sm font-semibold whitespace-nowrap px-3 py-1 border-2 border-dashed rounded-lg ${colors.borderActiveDashed} ${colors.textActive} bg-opacity-10 ${colors.bgButton}`}
              >
                {t("status.re-verification-request")}
              </div>
            </div>
          </div>
          {renderStepButtons()}
        </>
      ) : (
        // Show normal workflow steps for other steps
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-12" data-testid={tid("steps-track")}>
            {steps.slice(1).map((step, index) => (
              <React.Fragment key={step.label || index}>
                <div
                  className="relative flex flex-col items-center"
                  data-testid={tid(`step-${workflowStepSlug(step.label)}`)}
                >
                  <div>
                    {shouldShowCheckedIcon(index + 1, step) ? (
                      <CheckedIcon size={56} />
                    ) : (
                      step.icon && <step.icon size={56} isActive={getStepStatus(index + 1) === "current"} />
                    )}
                  </div>
                  <div
                    className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 min-w-30 max-[1600px]:max-w-[120px] text-center text-sm font-semibold whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:break-words ${getStepStatus(index + 1) === "current" && step.label !== "Ready" ? colors.textStatusActiveStep : colors.textPrimary}`}
                  >
                    {step.displayLabel}
                  </div>
                </div>
                {index < steps.slice(1).length - 1 && (
                  <div className={`${getConnectorClasses(index + 1)} max-w-40`} key={`connector-${index}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {renderStepButtons()}
        </>
      )}
      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid={tid("confirmation-modal")}
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
        cancelText={t("common.cancel-button")}
      />

      {/* Re-Verification Modal with DateTime Picker */}
      {reVerificationModal.isOpen && (
        <div
          data-testid={tid("re-verification-modal-overlay")}
          className={`fixed inset-0 z-[9998] flex items-center justify-start md:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
          onClick={closeReVerificationModal}
        >
          <div
            data-testid={tid("re-verification-modal")}
            className={`relative w-[448px] min-w-[448px] ${colors.bgModal} transform rounded-lg shadow-xl transition-all duration-200`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Content */}
            <div className="p-6">
              {/* Icon and Title */}
              <div className="mb-4 flex items-center justify-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center">
                  <CalendarIcon size={48} />
                </div>
              </div>

              {/* Title */}
              <div className="mb-4 text-center">
                <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2`}>
                  {t("common.re-verification-title")}
                </h3>

                {/* Description */}
                <p className={`text-sm ${colors.textSecondary} mb-4`}>{t("common.re-verification-with-date")}</p>

                {/* DateTime Picker */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                    {t("common.due-date-time")}
                  </label>
                  <CustomDatePicker
                    data-testid={tid("re-verification-datetime-picker")}
                    selected={reVerificationModal.selectedDateTime}
                    onChange={handleDateTimeChange}
                    minDate={new Date()}
                    className="w-full"
                    dateFormat="yyyy-MM-dd"
                    filterDate={(date) => {
                      const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                      return date >= today;
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3 flex-row justify-center">
                {/* Cancel Button */}
                <button
                  data-testid={tid("re-verification-cancel-button")}
                  onClick={closeReVerificationModal}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${colors.borderPrimary} ${colors.textSecondary} ${colors.bgSecondary} min-w-[100px] ${colors.focusRing2} focus:outline-none`}
                >
                  {t("common.cancel-button")}
                </button>

                {/* Confirm Button */}
                <PrimaryButton
                  data-testid={tid("re-verification-confirm-button")}
                  size="lg"
                  onClick={() => handleConfirmRequestReVerification(reVerificationModal.selectedDateTime)}
                  disabled={isRequestReVerificationLoading}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {t("common.re-verification-title")}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clarity Comparison Modal */}
      <SbomClarityCompareModal
        show={showClarityCompareModal}
        onHide={handleCloseClarityModal}
        projectSeq={projectSeq}
        onSelectVerification={handleSelectClarityVerification}
      />

      {/* SBOM Verification Request Modal */}
      <SbomVerificationRequest
        isOpen={sbomModal.isOpen}
        onClose={handleSbomModalClose}
        onSubmit={handleSbomReUpload}
      />

      {/* Duplicate Modal */}
      {duplicateModal.isOpen && (
        <ConfirmActionModal
          data-testid={tid("duplicate-file-modal")}
          isOpen={duplicateModal.isOpen}
          onClose={handleDuplicateModalClose}
          onConfirm={handleDuplicateConfirm}
          title={formatModalTitle(t("project.duplicate-file-warning"))}
          description={t("project.sbom-duplicate-file-warning-description")}
          confirmText={t("common.yes")}
          confirmButtonVariant="primary"
        />
      )}

      {/* Full Screen Loading */}
      <FullScreenDotLoader
        loading={isDuplicateCheckPending || processingRowId !== null || isReUploadPending}
        text={
          isDuplicateCheckPending
            ? t("project.verification-request.validating-file") || "Validating file..."
            : isReUploadPending
              ? t("project.verification-request.upload-verification-file") || "Uploading verification file..."
              : "Uploading verification file..."
        }
      />
    </div>
  );
};

export default WorkflowProgressSbom;
